// MARK://dhk8/admin-shell/pages/modules/CustomerDetailPage
// Ficha "ver-primero" de un cliente del CRM (Gustavo 2026-06-23). Header con inicial
// + nombre + origen + pills (nivel de lealtad) + KPIs (puntos, órdenes, total gastado)
// + Tabs (General, Pedidos, Loyalty[si hay programa], Historial). El tab General se ve
// primero; "Editar" abre el SectionedSheet con el MISMO formulario que el modal de la
// lista (datos personales + direcciones + entidades fiscales). Carga el cliente por id
// con getCustomer (clients/customers.ts) y la config de lealtad con getLoyaltyConfig.
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Plus, Trash2, MapPin, Receipt, Phone, Mail, Star } from 'lucide-react'

import {
  getCustomer, getLoyaltyConfig, updateCustomer,
  deriveTier, sourceLabel,
  type Customer, type LoyaltyConfig, type ContactAddress, type FiscalEntity, type CustomerInput,
} from '../../lib/clients/customers.js'
import { REGIMES } from '../../lib/clients/fiscal.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'pedidos' | 'loyalty' | 'historial'

const USOS = ['G01', 'G02', 'G03', 'I01', 'I04', 'P01', 'CP01', 'S01']
const ADDR_LABELS = [
  { value: 'home', label: 'Casa' },
  { value: 'work', label: 'Trabajo' },
  { value: 'other', label: 'Otra' },
]

function money(cents: string): string {
  const n = Number(cents) / 100
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}
function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}
function addrLabel(value: string): string {
  return ADDR_LABELS.find((l) => l.value === value)?.label ?? value
}
function regimeLabel(code: string): string {
  return REGIMES.find((r) => r.code === code)?.name ?? code
}

// ── editor state (mismo modelo que la lista) ─────────────────────────────────
interface DraftAddress extends Omit<ContactAddress, 'id'> { _k: string }
interface DraftFiscal extends Omit<FiscalEntity, 'id'> { _k: string }
interface Draft {
  firstName: string
  lastName: string
  notes: string
  phones: Array<{ _k: string; phone: string }>
  emails: Array<{ _k: string; email: string }>
  addresses: DraftAddress[]
  fiscalEntities: DraftFiscal[]
}

let keySeq = 0
const nk = () => `k${++keySeq}`

function draftFrom(c: Customer): Draft {
  return {
    firstName: c.firstName,
    lastName: c.lastName ?? '',
    notes: c.notes ?? '',
    phones: c.phones.map((p) => ({ _k: nk(), phone: p.phone })),
    emails: c.emails.map((e) => ({ _k: nk(), email: e.email })),
    addresses: c.addresses.map((a) => ({ _k: nk(), label: a.label, street: a.street, city: a.city, state: a.state, country: a.country, zip: a.zip, isPrimary: a.isPrimary })),
    fiscalEntities: c.fiscalEntities.map((f) => ({ _k: nk(), rfc: f.rfc, legalName: f.legalName, regimeCode: f.regimeCode, zipCode: f.zipCode, usoCfdi: f.usoCfdi, invoiceEmail: f.invoiceEmail, isDefault: f.isDefault })),
  }
}
function emptyAddress(): DraftAddress {
  return { _k: nk(), label: 'home', street: '', city: '', state: '', country: 'México', zip: '', isPrimary: false }
}
function emptyFiscal(): DraftFiscal {
  return { _k: nk(), rfc: '', legalName: '', regimeCode: '612', zipCode: '', usoCfdi: 'G03', invoiceEmail: '', isDefault: false }
}

function draftToInput(d: Draft): CustomerInput {
  const phones = d.phones.filter((p) => p.phone.trim()).map((p, i) => ({ phone: p.phone.trim(), label: 'mobile', isPrimary: i === 0 }))
  const emails = d.emails.filter((e) => e.email.trim()).map((e, i) => ({ email: e.email.trim(), label: 'work', isPrimary: i === 0 }))
  const addresses = d.addresses.map((a) => ({
    label: a.label,
    street: a.street?.trim() || null,
    city: a.city?.trim() || null,
    state: a.state?.trim() || null,
    country: a.country?.trim() || null,
    zip: a.zip?.trim() || null,
    isPrimary: a.isPrimary,
  }))
  const fiscalEntities = d.fiscalEntities.map((f) => ({
    rfc: f.rfc.trim().toUpperCase(),
    legalName: f.legalName.trim(),
    regimeCode: f.regimeCode,
    zipCode: f.zipCode.trim(),
    usoCfdi: f.usoCfdi,
    invoiceEmail: f.invoiceEmail?.trim() || null,
    isDefault: f.isDefault,
  }))
  return {
    firstName: d.firstName.trim(),
    lastName: d.lastName.trim() || undefined,
    notes: d.notes.trim() || undefined,
    phones, emails, addresses, fiscalEntities,
  }
}

export default function CustomerDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const customer = useAsync((s) => getCustomer(id, s), [id])
  const [loyalty, setLoyalty] = useState<LoyaltyConfig | null>(null)

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getLoyaltyConfig().then((c) => { if (alive) setLoyalty(c) }).catch(() => { /* lealtad opcional */ })
    return () => { alive = false }
  }, [])

  const c = customer.data
  const loyaltyOn = loyalty?.enabled ?? false
  const tier = c ? deriveTier(c.loyaltyPoints, loyalty) : null

  const phone = c ? (c.phones.find((x) => x.isPrimary) ?? c.phones[0])?.phone ?? null : null
  const email = c ? (c.emails.find((x) => x.isPrimary) ?? c.emails[0])?.email ?? null : null

  const avgTicketCents = useMemo(() => {
    if (!c || c.totalOrdersCount <= 0) return null
    return Math.round(Number(c.totalSpentCents) / c.totalOrdersCount)
  }, [c])

  const kpis = useMemo(() => {
    if (!c) return []
    const items: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }[] = [
      { label: 'Órdenes', value: c.totalOrdersCount.toLocaleString('es-MX') },
      { label: 'Total gastado', value: money(c.totalSpentCents) },
    ]
    if (loyaltyOn) items.push({ label: 'Puntos', value: c.loyaltyPoints.toLocaleString('es-MX'), tone: 'info' })
    return items
  }, [c, loyaltyOn])

  function openEdit() { if (c) { setDraft(draftFrom(c)); setSaveErr(null); setEditing(true) } }

  async function save() {
    if (!draft) return
    const input = draftToInput(draft)
    if (!input.firstName) { setSaveErr('El nombre es obligatorio.'); return }
    for (const f of input.fiscalEntities) {
      if (f.rfc.length < 10 || f.rfc.length > 13) { setSaveErr(`RFC inválido en entidad fiscal "${f.legalName || '(sin nombre)'}".`); return }
      if (!f.legalName) { setSaveErr('Toda entidad fiscal necesita razón social.'); return }
      if (f.zipCode.length !== 5) { setSaveErr(`CP inválido en entidad fiscal "${f.legalName}" (5 dígitos).`); return }
    }
    setSaving(true)
    setSaveErr(null)
    try {
      await updateCustomer(id, input)
      toast('Cliente actualizado', 'success')
      setEditing(false)
      customer.reload()
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  const tabs = useMemo(() => {
    const t = [{ id: 'general', label: 'General' }, { id: 'pedidos', label: 'Pedidos', count: c?.totalOrdersCount }]
    if (loyaltyOn) t.push({ id: 'loyalty', label: 'Lealtad' })
    t.push({ id: 'historial', label: 'Historial' })
    return t
  }, [c, loyaltyOn])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/customers/crm')} aria-label="Volver a clientes"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Cliente</h1><p className="adm-page__sub">CRM · ficha del cliente</p></div>
        </div>
      </div>

      {asyncState(customer.loading, customer.error, customer.reload)}

      {c && (
        <EntityDetailPage
          title={c.fullName || 'Sin nombre'}
          subtitle={[sourceLabel(c.source), phone, email].filter(Boolean).join(' · ')}
          pills={<>
            {loyaltyOn && tier && (
              <span className="adm-badge" style={tier.color ? { background: `${tier.color}22`, color: tier.color } : undefined}>{tier.name}</span>
            )}
            {loyaltyOn && (
              <StatusPill tone="info" label={`${c.loyaltyPoints} pts`} />
            )}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={tabs}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del cliente"
          />}
        >
          {tab === 'general' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
                <Field label="Nombre(s)">{c.firstName || <span className="adm-muted">—</span>}</Field>
                <Field label="Apellidos">{c.lastName || <span className="adm-muted">—</span>}</Field>
                <Field label="Origen">{sourceLabel(c.source)}</Field>
                <Field label="Notas internas">{c.notes || <span className="adm-muted">Sin notas</span>}</Field>
                <Field label="Etiquetas">
                  {c.tags.length > 0
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{c.tags.map((t) => <span key={t} className="adm-badge">{t}</span>)}</div>
                    : <span className="adm-muted">Sin etiquetas</span>}
                </Field>
              </div>

              <div className="adm-card adm-card--pad0">
                <div className="adm-list">
                  <div className="adm-row"><div className="adm-row__main"><div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Phone size={13} aria-hidden /> Teléfonos</div></div></div>
                  {c.phones.length === 0 && <div className="adm-empty">Sin teléfonos.</div>}
                  {c.phones.map((p, i) => (
                    <div key={p.id ?? i} className="adm-row">
                      <div className="adm-row__main"><div className="adm-row__title adm-mono">{p.phone}</div><div className="adm-row__meta">{p.label}</div></div>
                      {p.isPrimary && <div className="adm-row__tail"><StatusPill tone="neutral" label="Principal" /></div>}
                    </div>
                  ))}
                  <div className="adm-row"><div className="adm-row__main"><div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Mail size={13} aria-hidden /> Correos</div></div></div>
                  {c.emails.length === 0 && <div className="adm-empty">Sin correos.</div>}
                  {c.emails.map((e, i) => (
                    <div key={e.id ?? i} className="adm-row">
                      <div className="adm-row__main"><div className="adm-row__title adm-mono">{e.email}</div><div className="adm-row__meta">{e.label}</div></div>
                      {e.isPrimary && <div className="adm-row__tail"><StatusPill tone="neutral" label="Principal" /></div>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="adm-card adm-card--pad0">
                <div className="adm-list">
                  <div className="adm-row"><div className="adm-row__main"><div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><MapPin size={13} aria-hidden /> Direcciones</div></div></div>
                  {c.addresses.length === 0 && <div className="adm-empty">Sin direcciones registradas.</div>}
                  {c.addresses.map((a, i) => (
                    <div key={a.id ?? i} className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title">{[a.street, a.city, a.state].filter(Boolean).join(', ') || 'Dirección sin detalle'}</div>
                        <div className="adm-row__meta">{addrLabel(a.label)}{a.zip ? ` · C.P. ${a.zip}` : ''}{a.country ? ` · ${a.country}` : ''}</div>
                      </div>
                      {a.isPrimary && <div className="adm-row__tail"><StatusPill tone="neutral" label="Principal" /></div>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="adm-card adm-card--pad0">
                <div className="adm-list">
                  <div className="adm-row"><div className="adm-row__main"><div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Receipt size={13} aria-hidden /> Entidades fiscales</div></div></div>
                  {c.fiscalEntities.length === 0 && <div className="adm-empty">Sin entidades fiscales registradas.</div>}
                  {c.fiscalEntities.map((f, i) => (
                    <div key={f.id ?? i} className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title">{f.legalName || 'Sin razón social'}</div>
                        <div className="adm-row__meta"><span className="adm-mono">{f.rfc}</span> · {f.regimeCode} {regimeLabel(f.regimeCode)} · Uso {f.usoCfdi} · C.P. {f.zipCode}</div>
                      </div>
                      {f.isDefault && <div className="adm-row__tail"><StatusPill tone="neutral" label="Predeterminada" /></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'pedidos' && (
            <div className="adm-card adm-card--pad0">
              <div className="adm-list">
                <div className="adm-row">
                  <div className="adm-row__main"><div className="adm-row__title">Órdenes totales</div><div className="adm-row__meta">Histórico del cliente</div></div>
                  <div className="adm-row__tail"><span className="adm-mono">{c.totalOrdersCount.toLocaleString('es-MX')}</span></div>
                </div>
                <div className="adm-row">
                  <div className="adm-row__main"><div className="adm-row__title">Total gastado</div><div className="adm-row__meta">Suma de consumos</div></div>
                  <div className="adm-row__tail"><span className="adm-mono">{money(c.totalSpentCents)}</span></div>
                </div>
                {avgTicketCents != null && (
                  <div className="adm-row">
                    <div className="adm-row__main"><div className="adm-row__title">Ticket promedio</div><div className="adm-row__meta">Total / órdenes</div></div>
                    <div className="adm-row__tail"><span className="adm-mono">{money(String(avgTicketCents))}</span></div>
                  </div>
                )}
                <div className="adm-row">
                  <div className="adm-row__main"><div className="adm-row__title">Última visita</div><div className="adm-row__meta">Registro más reciente</div></div>
                  <div className="adm-row__tail"><span className="adm-mono">{formatDateTime(c.lastVisitAt)}</span></div>
                </div>
                {c.totalOrdersCount === 0 && (
                  <div className="adm-empty">Este cliente aún no tiene órdenes registradas.</div>
                )}
              </div>
            </div>
          )}

          {tab === 'loyalty' && loyaltyOn && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
                <Field label="Puntos de lealtad"><span className="adm-mono">{c.loyaltyPoints.toLocaleString('es-MX')}</span></Field>
                <Field label="Visitas"><span className="adm-mono">{c.loyaltyVisits.toLocaleString('es-MX')}</span></Field>
                <Field label="Nivel actual">
                  {tier
                    ? <span className="adm-badge" style={tier.color ? { background: `${tier.color}22`, color: tier.color } : undefined}>{tier.name}</span>
                    : <span className="adm-muted">Sin nivel</span>}
                </Field>
              </div>
              {(loyalty?.tiers.length ?? 0) > 0 && (
                <div className="adm-card adm-card--pad0">
                  <div className="adm-list">
                    <div className="adm-row"><div className="adm-row__main"><div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Star size={13} aria-hidden /> Niveles del programa</div></div></div>
                    {[...(loyalty?.tiers ?? [])].sort((a, b) => a.threshold - b.threshold).map((t, i) => {
                      const reached = c.loyaltyPoints >= t.threshold
                      return (
                        <div key={t.id ?? i} className="adm-row">
                          <div className="adm-row__main">
                            <div className="adm-row__title" style={t.color ? { color: t.color } : undefined}>{t.name}</div>
                            <div className="adm-row__meta">desde {t.threshold.toLocaleString('es-MX')} pts</div>
                          </div>
                          <div className="adm-row__tail"><StatusPill tone={reached ? 'ok' : 'neutral'} label={reached ? 'Alcanzado' : 'Pendiente'} /></div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={c.id} />}
        </EntityDetailPage>
      )}

      {draft && (
        <SectionedSheet
          open={editing}
          title="Editar cliente"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving || !draft.firstName.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          {saveErr && <div className="adm-banner adm-banner--danger">{saveErr}</div>}

          <FormSection title="Datos básicos">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label className="adm-field">
                <span className="adm-field__label">Nombre(s) *</span>
                <input className="adm-input" value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Apellidos</span>
                <input className="adm-input" value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
              </label>
            </div>
            <label className="adm-field">
              <span className="adm-field__label">Notas internas</span>
              <textarea className="adm-textarea" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </label>
          </FormSection>

          <FormSection title="Contacto">
            <RepeatGroup
              label="Teléfonos"
              addLabel="Agregar teléfono"
              items={draft.phones}
              onAdd={() => setDraft({ ...draft, phones: [...draft.phones, { _k: nk(), phone: '' }] })}
              onRemove={(k) => setDraft({ ...draft, phones: draft.phones.filter((p) => p._k !== k) })}
              renderItem={(p) => (
                <input
                  className="adm-input"
                  style={{ flex: 1 }}
                  placeholder="55 1234 5678"
                  value={p.phone}
                  onChange={(e) => setDraft({ ...draft, phones: draft.phones.map((x) => (x._k === p._k ? { ...x, phone: e.target.value } : x)) })}
                />
              )}
            />
            <RepeatGroup
              label="Correos"
              addLabel="Agregar correo"
              items={draft.emails}
              onAdd={() => setDraft({ ...draft, emails: [...draft.emails, { _k: nk(), email: '' }] })}
              onRemove={(k) => setDraft({ ...draft, emails: draft.emails.filter((e) => e._k !== k) })}
              renderItem={(em) => (
                <input
                  className="adm-input"
                  style={{ flex: 1 }}
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={em.email}
                  onChange={(e) => setDraft({ ...draft, emails: draft.emails.map((x) => (x._k === em._k ? { ...x, email: e.target.value } : x)) })}
                />
              )}
            />
          </FormSection>

          <FormSection title="Direcciones">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => setDraft({ ...draft, addresses: [...draft.addresses, emptyAddress()] })}>
                <Plus size={14} aria-hidden /> Agregar dirección
              </button>
            </div>
            {draft.addresses.length === 0 && <p className="adm-page__sub" style={{ margin: 0 }}>Sin direcciones registradas.</p>}
            {draft.addresses.map((a) => (
              <div key={a._k} className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <label className="adm-field" style={{ flex: 1 }}>
                    <span className="adm-field__label">Tipo</span>
                    <select
                      className="adm-select"
                      value={a.label}
                      onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, label: e.target.value } : x)) })}
                    >
                      {ADDR_LABELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </label>
                  <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar dirección" onClick={() => setDraft({ ...draft, addresses: draft.addresses.filter((x) => x._k !== a._k) })}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Calle y número</span>
                  <input className="adm-input" value={a.street ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, street: e.target.value } : x)) })} />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label className="adm-field">
                    <span className="adm-field__label">Ciudad</span>
                    <input className="adm-input" value={a.city ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, city: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">Estado</span>
                    <input className="adm-input" value={a.state ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, state: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">C.P.</span>
                    <input className="adm-input" value={a.zip ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, zip: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">País</span>
                    <input className="adm-input" value={a.country ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, country: e.target.value } : x)) })} />
                  </label>
                </div>
              </div>
            ))}
          </FormSection>

          <FormSection title="Información fiscal">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => setDraft({ ...draft, fiscalEntities: [...draft.fiscalEntities, emptyFiscal()] })}>
                <Plus size={14} aria-hidden /> Agregar entidad
              </button>
            </div>
            <p className="adm-page__sub" style={{ margin: 0 }}>
              Razones sociales a las que se le emite factura. Opcional — un cliente puede registrar varias.
            </p>
            {draft.fiscalEntities.map((f) => (
              <div key={f._k} className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <label className="adm-field" style={{ flex: 1 }}>
                    <span className="adm-field__label">RFC *</span>
                    <input
                      className="adm-input"
                      value={f.rfc}
                      maxLength={13}
                      onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, rfc: e.target.value.toUpperCase() } : x)) })}
                    />
                  </label>
                  <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar entidad fiscal" onClick={() => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.filter((x) => x._k !== f._k) })}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Razón social *</span>
                  <input className="adm-input" value={f.legalName} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, legalName: e.target.value } : x)) })} />
                </label>
                <label className="adm-field">
                  <span className="adm-field__label">Régimen fiscal</span>
                  <select className="adm-select" value={f.regimeCode} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, regimeCode: e.target.value } : x)) })}>
                    {REGIMES.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
                  </select>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label className="adm-field">
                    <span className="adm-field__label">C.P. fiscal *</span>
                    <input className="adm-input" value={f.zipCode} maxLength={5} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, zipCode: e.target.value.replace(/\D/g, '') } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">Uso de CFDI</span>
                    <select className="adm-select" value={f.usoCfdi} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, usoCfdi: e.target.value } : x)) })}>
                      {USOS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Correo para facturas</span>
                  <input className="adm-input" type="email" value={f.invoiceEmail ?? ''} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, invoiceEmail: e.target.value } : x)) })} />
                </label>
              </div>
            ))}
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}

// Grupo repetible para listas simples (teléfonos, correos).
function RepeatGroup<T extends { _k: string }>(props: {
  label: string
  addLabel: string
  items: T[]
  onAdd: () => void
  onRemove: (k: string) => void
  renderItem: (item: T) => React.ReactNode
}) {
  return (
    <div className="adm-field">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="adm-field__label">{props.label}</span>
        <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={props.onAdd}>
          <Plus size={13} aria-hidden /> {props.addLabel}
        </button>
      </div>
      {props.items.map((it) => (
        <div key={it._k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {props.renderItem(it)}
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar" onClick={() => props.onRemove(it._k)}>
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      ))}
    </div>
  )
}
